import React, { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";

const Backdrop = ({ onClick }) => (
  <div onClick={onClick} className="fixed inset-0 bg-black/40 z-[110]"></div>
);

const ModalContainer = ({ children }) => (
  <div className="fixed inset-y-0 pb-[100px] md:pb-0 top-[100px] md:top-0 right-0 z-[110] flex h-full w-full md:w-[520px] lg:w-[724px]">
    <div className="ml-auto h-full w-full bg-white rounded md:rounded-[24px] border border-l-0 border-[#DCEDFF] overflow-y-auto">
      {children}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-subsection-title font-rubik md:text-section-title-d text-esdarkGray font-[500]">
    {children}
  </h3>
);

const Field = ({ label, children }) => (
  <div className="w-full">
    <label className="block text-caption opacity-50 text-esDarkBlue mb-2">
      {label}
    </label>
    {children}
  </div>
);

const CreateListingModal = ({
  open,
  onClose,
  onSave,
  isLoading = false,
  error = null,
}) => {
  const MAX_IMAGES = 3;
  const MAX_FILE_SIZE_MB = 15;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]); // {id, url, file}
  const [imageError, setImageError] = useState("");
  const [propName, setPropName] = useState("");
  const [propValue, setPropValue] = useState("");
  const [properties, setProperties] = useState([]); // [{id, name, value}]
  const [propError, setPropError] = useState("");

  // Form data state
  const [formData, setFormData] = useState({
    title: "",
    plateNumber: "",
    category: "",
    customCategory: "",
    price: "",
    description: "",
    location: "",
  });

  const handleChooseFiles = () => fileInputRef.current?.click();

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);

    const mapped = validFiles.map((file, idx) => ({
      id: `${Date.now()}-${idx}`,
      url: URL.createObjectURL(file),
      file,
    }));

    if (oversized.length) {
      setImageError(
        `Some files were skipped because they exceed ${MAX_FILE_SIZE_MB}MB.`
      );
    } else {
      setImageError("");
    }

    setImages((prev) => {
      const combined = [...prev, ...mapped];
      return combined.slice(0, MAX_IMAGES);
    });
  };

  const handleRemoveImage = (id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleAddProperty = () => {
    const name = propName.trim();
    const value = propValue.trim();
    if (!name || !value) {
      setPropError("Please enter both name and value");
      return;
    }
    const exists = properties.some(
      (p) =>
        p.name.toLowerCase() === name.toLowerCase() &&
        String(p.value).toLowerCase() === value.toLowerCase()
    );
    if (exists) {
      setPropError("This property is already added");
      return;
    }
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setProperties((prev) => [...prev, { id, name, value }]);
    setPropName("");
    setPropValue("");
    setPropError("");
  };

  const handleRemoveProperty = (id) => {
    setProperties((prev) => prev.filter((p) => p.id !== id));
  };

  const handleInputChange = (field, value) => {
    // If category changes away from car, clear plate number
    if (field === "category") {
      setFormData((prev) => ({
        ...prev,
        category: value,
        plateNumber: value === "car" ? prev.plateNumber : "",
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    // Validate required fields
    // if (!formData.title || !formData.category || !formData.price) {
    //   alert("Please fill in all required fields");
    //   return;
    // }

    // // Validate custom category when "other" is selected
    // if (formData.category === "other" && !formData.customCategory?.trim()) {
    //   alert("Please enter a custom category name");
    //   return;
    // }

    // Prepare data for API
    const listingData = {
      title: formData.title,
      plateNumber:
        formData.category === "car"
          ? (formData.plateNumber || "").trim()
          : null,
      category: formData.category,
      customCategory: formData.customCategory,
      price: parseFloat(formData.price),
      description: formData.description,
      location: formData.location || "",
      additionalProperties: properties.map((p) => ({
        name: p.name,
        value: p.value,
      })),
      images: images.map((img) => img.file), // Send actual file objects
      // Keep compatibility with backend replace logic: do not delete any existing images implicitly.
      replace_existing_images: false,
    };

    // Debug: log location to verify it's being captured
    console.log("CreateListingModal - Location value:", listingData.location);
    console.log("CreateListingModal - Full listingData:", listingData);

    // Call the onSave function with the data
    onSave(listingData);
  };

  // Reset all local form state
  const resetFormState = () => {
    setImages([]);
    setPropName("");
    setPropValue("");
    setProperties([]);
    setPropError("");
    setFormData({
      title: "",
      plateNumber: "",
      category: "",
      customCategory: "",
      price: "",
      description: "",
      location: "",
    });
  };
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  // When modal closes (open becomes false), clear the form for next time
  useEffect(() => {
    if (!open) {
      resetFormState();
    }
  }, [open]);

  if (!open) return null;

  return (
    <>
      <Backdrop onClick={onClose} />
      <ModalContainer>
        <div className="sticky top-0 z-10 flex items-center justify-between py-5 md:py-8 px-5 md:px-16 bg-white">
          <div>
            <h2 className="text-page-title font-rubik md:text-page-title-d text-esdarkGray font-semibold">
              Create a new listing
            </h2>
            <p className="text-page-subtitle font-normal mt-4 text-esDarkBlue opacity-50">
              Find all of your listings below.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="#1E1E1E"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="px-5 md:px-16 space-y-8">
          <section className="space-y-4">
            <SectionTitle>Basic information</SectionTitle>
            <Field label="Item title">
              <input
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                placeholder="e.g. Name of the item"
              />
            </Field>

            <Field label="Category">
              <div className="space-y-3">
                <select
                  value={formData.category}
                  onChange={(e) =>
                    handleInputChange("category", e.target.value)
                  }
                  className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                >
                  <option value="">Select category</option>
                  <option value="car">Car</option>
                  <option value="bag">Bag</option>
                  <option value="watch">Watch</option>
                  <option value="real_estate">Real estate</option>
                  <option value="other">Other</option>
                </select>
                {formData.category === "other" && (
                  <Field label="Custom category">
                    <input
                      value={formData.customCategory}
                      onChange={(e) =>
                        handleInputChange("customCategory", e.target.value)
                      }
                      className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                      placeholder="Enter custom category (e.g. Computer)"
                      required
                    />
                  </Field>
                )}
              </div>
            </Field>
            {formData.category === "car" && (
              <Field label="Plate number">
                <input
                  value={formData.plateNumber}
                  onChange={(e) =>
                    handleInputChange("plateNumber", e.target.value)
                  }
                  className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                  placeholder="e.g. 00-BC-22"
                />
              </Field>
            )}
            <Field label="Price">
              <input
                type="number"
                min="0"
                step="1"
                value={formData.price || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow empty string or valid positive numbers
                  if (val === "" || (!isNaN(val) && parseFloat(val) >= 0)) {
                    handleInputChange("price", val);
                  }
                }}
                className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                placeholder="e.g. 45000"
              />
            </Field>
            <Field label="Location">
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                placeholder="e.g. Amsterdam, Netherlands"
              />
            </Field>
            <Field label="Description">
              <textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                className="w-full min-h-[84px] rounded-[8px] border border-gray-200 px-3 py-3"
                placeholder="Detailed description of your item..."
              ></textarea>
            </Field>
          </section>

          <section className="space-y-4">
            <SectionTitle>Additional information</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Property name">
                <input
                  value={propName}
                  onChange={(e) => setPropName(e.target.value)}
                  className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                  placeholder="e.g. Name of the property"
                />
              </Field>
              <div className="flex gap-4 items-end">
                <Field label="Property value">
                  <input
                    value={propValue}
                    onChange={(e) => setPropValue(e.target.value)}
                    className="w-full rounded-[8px] border border-gray-200 px-3 py-3"
                    placeholder="e.g. Value of the property"
                  />
                </Field>
                <button
                  type="button"
                  onClick={handleAddProperty}
                  className="mb-[2px] h-[50px] w-[60px] inline-flex items-center justify-center rounded-[8px] border border-[#DCEDFF] text-esblue"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
            {propError && (
              <div className="text-[12px] text-red-600">{propError}</div>
            )}

            {properties.length > 0 && (
              <div className="mt-4 space-y-4">
                {properties.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-[12px] border border-[#F4E5E5] bg-[#FFF7F7]/30 px-4 py-2 text-[#1E1E1E]"
                  >
                    <div className="opacity-60">{p.name}</div>
                    <div className="flex items-center gap-3">
                      <div className="font-semibold opacity-80">{p.value}</div>
                      <button
                        onClick={() => handleRemoveProperty(p.id)}
                        className="h-[42px] w-[42px] rounded-[8px] bg-red-100 text-red-600 flex items-center justify-center"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M18 6L6 18M6 6l12 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-4">
            <SectionTitle>Images</SectionTitle>
            <div className="rounded-[12px] border-2 border-dashed border-[#CFE3FF] p-8 text-center">
              <div className="mx-auto mb-4 rounded-full h-10 w-10 flex items-center justify-center bg-[#F0F6FF]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 3v10"
                    stroke="#2A8BF7"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.5 6.5L12 3l3.5 3.5"
                    stroke="#2A8BF7"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 14v3a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-3"
                    stroke="#2A8BF7"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="text-sm text-esDarkBlue">
                Drag and drop images here, or click to
                <br />
                select.
              </p>
              <div className="mt-4">
                <input
                  ref={fileInputRef}
                  onChange={handleFilesSelected}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                />
                <button
                  onClick={handleChooseFiles}
                  disabled={images.length >= MAX_IMAGES}
                  className={`w-full px-4 py-2 rounded-[8px] font-semibold ${
                    images.length >= MAX_IMAGES
                      ? "bg-[#F2F6FF] text-gray-400 cursor-not-allowed"
                      : "bg-[#F2F6FF] hover:bg-[#EAF3FF] text-esblue"
                  }`}
                >
                  Choose files
                </button>
                <div className="mt-2 text-[12px] text-esDarkBlue opacity-50">
                  Max {MAX_IMAGES} images. Max size: {MAX_FILE_SIZE_MB}mb.
                </div>
                {imageError && (
                  <div className="mt-2 text-[12px] text-esDarkBlue opacity-50">
                    {imageError}
                  </div>
                )}
              </div>
            </div>
            {images.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-3">
                {images.map((img) => (
                  <div
                    key={img.id}
                    className="relative rounded-[8px] overflow-hidden border border-[#DCEDFF] h-[80px] bg-gray-100"
                  >
                    <img
                      src={img.url}
                      alt="preview"
                      className="h-full w-full object-cover"
                    />
                    <button
                      onClick={() => handleRemoveImage(img.id)}
                      className="absolute top-2 right-2 h-6 w-6 rounded-md bg-red-100 text-red-600 flex items-center justify-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M18 6L6 18M6 6l12 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-[8px] p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="py-6 px-5 md:px-16 border-t border-[#F0F6FF] rounded-bl-[16px] bg-white">
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-[8px] text-white text-sm ${
              isLoading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-gradient hover:opacity-90"
            }`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Creating listing...</span>
              </>
            ) : (
              <>
                <span>＋</span>
                <span>Publish listing</span>
              </>
            )}
          </button>
        </div>
      </ModalContainer>
    </>
  );
};

export default CreateListingModal;
